import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Gauge, Cpu, ShieldCheck, ArrowRight } from "lucide-react";
import { Seo, breadcrumbSchema, faqSchema, shopNode } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TrustStrip } from "../components/TrustStrip";
import { FaqAccordion } from "../components/FaqAccordion";
import { DmeForm } from "../components/DmeForm";
import { CtaBlock } from "../components/CtaBlock";
import { Reveal, Eyebrow } from "../components/Reveal";
import { BIZ, LAST_UPDATED, track, cleanText } from "../data/site";
import { Img } from "../components/Img";



const BRANDS = ["BMW", "BMW M", "Porsche", "Mercedes-AMG", "Audi RS", "McLaren", "Lamborghini", "Ferrari", "Bentley", "Aston Martin"];

const SECTIONS = [
  {
    q: "What is DME tuning and what does it do?",
    lead: "DME tuning recalibrates your engine control module — fueling, boost, ignition timing, and throttle maps — to unlock the performance the factory left on the table.",
    body: "The results you feel immediately: sharper throttle response, stronger mid-range torque, and smoother, more eager drivability — not just a peak horsepower number. Every tune we deliver is street-legal and emissions-compliant [PLACEHOLDER: tuning platforms/software used, e.g. Bootmod3, MHD, COBB]. Typical gains vary by platform [PLACEHOLDER: typical horsepower gains by model], and we walk you through expected results for your exact vehicle before any work begins.",
  },
  {
    q: "Do you repair or replace a failed DME/ECU?",
    lead: "Yes — we diagnose, repair, and clone DME/ECU units, which is often far cheaper than a new module from the dealer.",
    body: "Common symptoms of a failing DME include no-start conditions, limp mode, persistent check engine lights, misfire codes that ignore new coils and plugs, and modules that stop communicating with scan tools entirely. Our process starts with proper electrical diagnostics — not parts darts. When a module is genuinely failed, we repair at board level or clone your data onto a replacement unit and code it to your vehicle, preserving immobilizer and adaptation data.",
  },
  {
    q: "Which cars do you tune and repair?",
    lead: "We specialize in BMW / BMW M, Porsche, Mercedes-AMG, Audi RS, McLaren, Lamborghini, Ferrari, Bentley, and Aston Martin.",
    body: "These platforms share one trait: sophisticated engine management that rewards expertise and punishes guesswork. Our technicians work with factory-level diagnostic equipment and brand-specific procedures, whether we are tuning an S58, repairing a Bosch DME from a 911, or calibrating an AMG after collision repair. If your performance marque is not listed, call (214) 888-2886 — if we cannot serve it properly, we will tell you who can.",
  },
  {
    q: "Will tuning void my warranty or affect inspection?",
    lead: "Honest answer: tuning can affect powertrain warranty coverage, and we explain the implications for your specific vehicle before any work — and yes, we only deliver emissions-compliant, street-legal tunes that pass Texas inspection.",
    body: "Magnuson-Moss protects you from blanket warranty denial, but a manufacturer can decline a powertrain claim linked to a modification. We lay out exactly what that means for your car, your coverage, and your risk tolerance first. Every tune we sell keeps all factory emissions systems fully functional — we do not offer, and will not discuss, anything else. Vehicles leave our shop able to pass Texas state inspection as delivered.",
  },
  {
    q: "Can you repair collision damage on high-end and exotic cars?",
    lead: "Yes — collision repair for luxury and exotic vehicles is our foundation: OEM repair procedures, a computerized frame machine, and ADAS recalibration under one roof.",
    body: "Exotic structures — bonded aluminum, surface fiber tubs, mixed-material chassis — demand the manufacturer's own repair method, not a generalist's habit. We follow OEM procedures, verify structure with printed laser measurements, match finishes with Sherwin-Williams spectrophotometer readings, and recalibrate every driver-assistance system before delivery. Documented, factory-standard repairs are also what preserve your vehicle's resale and collector value.",
  },
  {
    q: "Why choose Westway for performance and luxury vehicles?",
    lead: "Because one facility handles both sides of your car: certified collision restoration to factory standard, and performance DME work by specialists who understand these platforms.",
    body: "An I-CAR Gold Class collision team, a computerized frame machine, a time-efficient paint booth, and DME diagnostics under one roof means no handoffs between strangers. Every repair carries a written lifetime warranty; every tune is documented with before/after verification [PLACEHOLDER: real before/after dyno results]. Your vehicle is stored indoors, insured, and treated like it is ours.",
  },
];

const PROCESS = [
  { t: "Consultation", d: "Your goals, your vehicle's history, and honest expectations — before any commitment." },
  { t: "Diagnostics & data log", d: "Full scan and baseline logging so decisions rest on data, not guesses." },
  { t: "Tune or repair", d: "Emissions-compliant calibration, board-level DME repair, or module cloning." },
  { t: "Road test & verification", d: "Post-work logging and road testing confirm the result." },
  { t: "Documented handover", d: "You leave with records of everything measured and changed." },
];

const FAQS = [
  { q: "How much does DME tuning cost?", a: "[PLACEHOLDER: tuning and DME repair pricing ranges.] Every consultation starts free — call (214) 888-2886 or use the inquiry form on this page." },
  { q: "How much power will my car gain?", a: "Gains depend on platform, fuel, and supporting modifications [PLACEHOLDER: typical horsepower gains by model]. We quote expected results for your exact vehicle before any work." },
  { q: "Is your tuning emissions-legal in Texas?", a: "Yes. We only deliver street-legal, emissions-compliant calibrations that keep all factory emissions equipment functional and pass Texas inspection." },
  { q: "Can a dead DME really be repaired instead of replaced?", a: "Often, yes — board-level repair or data cloning onto a donor unit is routinely a fraction of dealer replacement cost. Diagnostics confirm which path your module needs." },
  { q: "Do you tune vehicles still under factory warranty?", a: "We explain warranty implications honestly before any tuning — the decision is always yours, made with full information." },
  { q: "Can you combine collision repair with performance work in one visit?", a: "Yes — that is our signature. Many clients add a documented, emissions-compliant tune while their vehicle is with us for certified collision restoration." },
  { q: "How long does DME work take?", a: "Tuning is typically [PLACEHOLDER: tuning turnaround]; DME repair and cloning depends on the fault [PLACEHOLDER: DME repair turnaround]. We confirm timelines at consultation." },
];

const RELATED = [
  ["Frame Straightening", "/services/frame-straightening"],
  ["Paint Refinishing", "/services/paint-refinishing"],
  ["ADAS Calibration", "/services/adas-calibration"],
  ["Collision Repair", "/services/collision-repair"],
  ["Free Estimate", "/free-estimate"],
];

export default function DmePage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  const schemas = [
    ...["DME Tuning", "ECU Repair", "Performance Tuning", "Luxury Vehicle Collision Repair"].map((t) => ({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: t,
      name: `${t} in Carrollton, TX`,
      url: "https://wwcollision.com/dme-tuning-dallas",
      provider: shopNode,
      areaServed: ["Carrollton", "Dallas", "Plano", "Irving", "Frisco"].map((c) => ({ "@type": "City", name: c })),
    })),
    faqSchema(FAQS),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Performance & DME Tuning", path: "/dme-tuning-dallas" },
    ]),
  ];

  return (
    <div data-testid="dme-page">
      <Seo
        title="DME Tuning & Exotic Collision Repair Carrollton | Westway"
        description="DME tuning, ECU repair & exotic collision repair in Carrollton, TX, serving Dallas & the DFW Metroplex. BMW M, Porsche, AMG specialists. Call (214) 888-2886."
        path="/dme-tuning-dallas"
        schemas={schemas}
      />

      <section ref={ref} className="relative min-h-[92svh] flex items-end overflow-hidden noise-overlay">
        <motion.div className="absolute inset-0 will-change-transform" style={{ y: imgY }}>
          <Img name="performance-tuning-carrollton-tx" alt="Performance parts — clutches, brakes and suspension components — prepared for tuning at Westway Collision Center in Carrollton, TX" eager bleed className="h-full w-full object-cover" sizes="100vw" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/25" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/35 to-transparent" aria-hidden="true" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-36 pb-16">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Performance & DME Tuning", path: "/dme-tuning-dallas" }]} />
          <Reveal>
            <Eyebrow>Flagship Performance Division</Eyebrow>
            <h1 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.06] max-w-5xl text-balance">
              DME Tuning, Repair &amp; Performance Collision Repair in Carrollton, TX
            </h1>
            <p className="mt-6 max-w-3xl text-base sm:text-lg text-slate-300 leading-relaxed">
              A DME — Digital Motor Electronics — is the engine control module at the heart of BMW,
              Porsche, Mercedes-AMG and other performance brands. Westway Collision Center handles
              both sides of that world: emissions-compliant performance tuning and expert DME repair,
              alongside factory-standard collision restoration for high-end, luxury, and exotic
              vehicles — based in Carrollton and serving Dallas and the DFW Metroplex.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href="#dme-inquiry"
                data-testid="dme-hero-form-cta"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector("#dme-inquiry");
                  if (el && window.lenis) window.lenis.scrollTo(el, { offset: -80, duration: 1.6 });
                }}
                className="inline-flex items-center justify-center gap-3 rounded-full bg-accentdeep px-8 py-4 font-display font-bold text-sm sm:text-base text-white transition-all duration-300 hover:bg-accenthover hover:-translate-y-0.5"
              >
                <Gauge size={17} />
                Request a Consultation
              </a>
              <a
                href={BIZ.phoneHref}
                data-testid="dme-hero-call-cta"
                onClick={() => track("click_to_call", { location: "dme_hero" })}
                className="inline-flex items-center justify-center gap-3 rounded-full border border-white/25 bg-white/[0.04] backdrop-blur-md px-8 py-4 font-display font-semibold text-sm sm:text-base text-white transition-all duration-300 hover:border-accent/60 hover:text-accent"
              >
                <Phone size={16} />
                Call {BIZ.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <TrustStrip />

      <section className="py-16 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-8">
            <Eyebrow>Marques We Serve</Eyebrow>
          </Reveal>
          <div className="flex flex-wrap justify-center gap-3">
            {BRANDS.map((b) => (
              <span key={b} className="rounded-full border border-white/12 bg-surface px-5 py-2.5 font-display text-sm sm:text-base font-semibold text-slate-200 tracking-wide">
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            ["dodge-ram-performance-upgrade", "Dodge Ram performance upgrade at Westway Collision Center in Carrollton, TX"],
            ["corvette-repair-after-carrollton", "Corvette finished after performance work at Westway Collision Center in Carrollton, TX"],
            ["mercedes-g-wagon-westway-collision", "Mercedes G-Wagon performance service at Westway Collision Center in Carrollton, TX"],
          ].map(([n, a], i) => (
            <Reveal key={n} delay={i * 0.06}>
              <div className="clip-frame overflow-hidden rounded-sm border border-white/10">
                <Img name={n} alt={a} className="h-56 sm:h-64 w-full object-cover" sizes="(min-width: 640px) 33vw, 100vw" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {SECTIONS.map((sec, i) => (
            <Reveal key={sec.q} className={i > 0 ? "mt-14" : ""}>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white text-balance">{sec.q}</h2>
              <p className="mt-4 text-sm sm:text-base text-slate-200 leading-relaxed"><strong className="text-body font-semibold">{cleanText(sec.lead)}</strong></p>
              <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">{cleanText(sec.body)}</p>
              {i === 1 && (
                <div className="clip-frame mt-8 overflow-hidden rounded-sm border border-white/10">
                  <Img name="lamborghini-huracan-performance-tuning" alt="Lamborghini Huracan performance tuning at Westway Collision Center in Carrollton, TX" className="block mx-auto h-64 w-full object-cover" sizes="(min-width: 1024px) 60vw, 100vw" />
                </div>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-20 lg:py-24 surface-panel border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Step By Step</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">Our Performance Process</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-white/[0.08] bg-surface p-6">
                  <span className="font-display text-3xl font-extrabold accent-text">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display text-base font-semibold text-slate-100">{p.t}</h3>
                  <p className="mt-2 text-xs sm:text-sm text-slate-400 leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="dme-inquiry" className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-2xl border border-accent/25 bg-gradient-to-br from-accent/[0.07] via-surface to-surface p-7 sm:p-10">
              <Cpu size={28} className="text-accent" strokeWidth={1.5} aria-hidden="true" />
              <h2 className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">
                Performance &amp; DME Inquiry
              </h2>
              <p className="mt-3 text-sm sm:text-base text-slate-400">
                Tell us the vehicle, the goal, and what it has now — a specialist responds with an
                honest plan, usually within one business hour.
              </p>
              <div className="mt-8">
                <DmeForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10">
            <Eyebrow>Straight Answers</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">DME Tuning &amp; Repair FAQs</h2>
          </Reveal>
          <FaqAccordion faqs={FAQS} idPrefix="faq-dme" />
        </div>
      </section>

      <section className="pb-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-8">
            <Eyebrow>Related Services</Eyebrow>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {RELATED.map(([label, to]) => (
              <Reveal key={to}>
                <Link to={to} data-testid={`dme-related-${to.replace(/\//g, "-")}`} className="group flex h-full items-center justify-between gap-3 rounded-2xl border border-white/[0.08] bg-surface p-5 transition-all duration-500 hover:border-accent/30 hover:-translate-y-1">
                  <span className="font-display text-sm sm:text-base font-semibold text-slate-100 group-hover:text-accent transition-colors">{label}</span>
                  <ArrowRight size={15} className="shrink-0 text-mute group-hover:text-accent transition-colors" aria-hidden="true" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CtaBlock heading="Talk To a Performance Specialist" sub="Honest advice on tuning, DME repair, or certified collision restoration for your vehicle — free, and without obligation." />
          <p className="mt-8 text-center font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>
    </div>
  );
}
